/**
 * Iter 113.4 — AI Configuration page (super_admin only)
 *
 * Single card listing every AI-powered feature in the platform with an on/off
 * toggle. Read-only for non-super-admins (they can still see what's enabled,
 * just not change it).
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Save, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export default function AIConfiguration() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [config, setConfig] = useState({});
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet('/settings/ai-config');
      setCatalog(res.catalog || []);
      setConfig(res.config || {});
      setCanEdit(!!res.can_edit);
    } catch (e) {
      toast.error('Could not load AI configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (key) => {
    if (!canEdit) return;
    setConfig((c) => ({ ...c, [key]: !c[key] }));
  };

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await apiPut('/settings/ai-config', { config });
      setConfig(res.config || {});
      toast.success('AI configuration saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Loading AI configuration…
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="ai-config-page">
      <div className="flex items-start gap-3 mb-1">
        <Sparkles className="text-amber-500 shrink-0 mt-1" size={26} />
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Settings · AI Configuration</div>
          <h1 className="text-2xl md:text-3xl font-display font-extrabold tracking-tight text-[#0F172A] mt-1">AI Features</h1>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Globally enable or disable each AI-powered feature in the platform.
            Disabled features fall back to manual / heuristic behaviour and do not
            consume the Ellora AI budget.
          </p>
        </div>
      </div>

      {!canEdit && (
        <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-2 text-sm text-amber-800">
          <Lock size={15} className="shrink-0" />
          <span>Read-only — only <b>Super Admin</b> can change AI feature toggles.</span>
        </div>
      )}

      <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm divide-y divide-slate-100">
        {catalog.map((f) => {
          const on = !!config[f.key];
          return (
            <div key={f.key} className="px-5 py-4 flex items-start gap-4" data-testid={`ai-feature-${f.key}`}>
              <button
                type="button"
                onClick={() => toggle(f.key)}
                disabled={!canEdit}
                role="switch"
                aria-checked={on}
                data-testid={`ai-feature-toggle-${f.key}`}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                  on ? 'bg-emerald-500' : 'bg-slate-300'
                } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    on ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-sm text-[#0F172A]">{f.label}</div>
                  {f.default ? null : (
                    <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-bold">
                      Default OFF
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-snug">{f.description}</p>
              </div>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${on ? 'text-emerald-600' : 'text-slate-400'}`}>
                {on ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="ai-config-save"
            className="px-5 py-2.5 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm hover:bg-[#2e3a5d] disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Configuration
          </button>
        </div>
      )}
    </div>
  );
}
